import React from 'react';
import { color, radius, TOUCH } from '../tokens';
import { haptic } from '../../telegram';

export interface Choice<T extends string = string> {
  key: T;
  label: string;
}

interface ChoiceChipsProps<T extends string> {
  options: Choice<T>[];
  value?: T | null;
  onChange: (key: T) => void;
  /** Stack full-width (one answer per row) instead of wrapping inline. */
  stack?: boolean;
}

function containerStyle(stack?: boolean): React.CSSProperties {
  return {
    display: 'flex',
    flexDirection: stack ? 'column' : 'row',
    flexWrap: stack ? 'nowrap' : 'wrap',
    gap: 8,
  };
}

function chipStyle(active: boolean, stack?: boolean): React.CSSProperties {
  return {
    minHeight: TOUCH,
    padding: stack ? '12px 16px' : '10px 16px',
    borderRadius: stack ? radius.md : radius.pill,
    textAlign: stack ? 'left' : 'center',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    background: active ? color.accentSoft : color.surfaceRaised,
    color: active ? color.accent : color.text,
    border: `1px solid ${active ? color.accent : color.border}`,
    WebkitTapHighlightColor: 'transparent',
  };
}

/**
 * Answer chips for the clarification wizard. Large tap targets (>=44px),
 * theme-aware, single-select.
 */
export default function ChoiceChips<T extends string>({ options, value, onChange, stack }: ChoiceChipsProps<T>) {
  return (
    <div style={containerStyle(stack)}>
      {options.map((opt) => {
        const active = opt.key === value;
        const pick = () => { onChange(opt.key); haptic('light'); };
        return (
          <button key={opt.key} type="button" onClick={pick} style={chipStyle(active, stack)}>
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
